import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { isTenantSuspendedError } from "@/lib/apiErrors";
import { tenantHomePath } from "@/lib/tenantPath";

/**
 * NO PAYMENT-READINESS SIGNAL LIVES HERE, DELIBERATELY.
 *
 * This context used to carry `paymentsReady` — the practice's own Stripe Connect
 * onboarding state, read from `/public/stripe-connect/config` (falling back to
 * `/public/site`, and to `false` when both failed). LearnPlayer, Reserver and
 * Consultations gated their paid flows on it.
 *
 * That was wrong on the money model. Solunary is the MERCHANT OF RECORD: every customer
 * charge settles into the platform's own Stripe balance (`_rail_route` in
 * modules/payments/service.py returns "platform" unconditionally), the practitioner is
 * credited in Solunary's ledger, and the connected account is only a PAYOUT destination —
 * a practice without one still sells, its payout simply parks in `held`. No charge path
 * reads the flag, and the `409 CONNECT_NOT_READY` refusal those gates claimed to
 * anticipate is raised nowhere in the backend. So the flag could only ever refuse sales
 * the platform was fully able to collect, and its fail-closed default turned a transient
 * failure of two GETs into a shop with no checkout (the 2026-08-10 outage).
 *
 * The signal itself is not a lie, it is simply not the public's business: the practitioner
 * reads their own onboarding state on the admin side, from `/tenant/stripe-connect/status`
 * (admin/payments/StripeConnectCard.jsx). Re-introducing it here would re-introduce the
 * temptation to gate on it.
 */
const TenantContext = createContext({
  tenant: null,
  loading: true,
  isDefault: true,
  unavailable: false,
  refresh: async () => {},
});

export const TenantProvider = ({ slug, children }) => {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  // BUG-041 — WHY the bootstrap failed, not just that it did. `tenant: null` covers two
  // opposite situations: the practice exists but the platform is not serving it
  // (suspended, `pending`, `cancelled` — 403 TENANT_SUSPENDED, the same rule
  // tenant_middleware applies to a tenant host), or no practice answers to this slug at
  // all (404). Collapsed into one outcome, `/t/{slug}` told a suspended practice's own
  // clients the address was dead. The consumer of the distinction is
  // tenant/TenantSite.jsx's ServedSiteGate.
  const [unavailable, setUnavailable] = useState(false);
  const effectiveSlug = (slug || "emilie").toLowerCase();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/saas/tenant/${effectiveSlug}`);
      // A 200 IS NOT A TENANT (BUG-039 re-open). The production edge serves 200 plus
      // `index.html` for any path it does not recognise, `/api/...` included, so a
      // deploy where this route is missing or briefly unrouted answers a STRING of HTML
      // that every reader downstream then treats as a tenant record: `tenant.name` is
      // undefined (the confirmation page addresses "Notre équipe" instead of the
      // practice), `tenant.branding` is undefined, and `tenant.is_root` is undefined —
      // which is what sent a client clicking "Back to home" to the SaaS marketing page.
      //
      // The predicate is deliberately the TYPE and nothing more: `null` — "we do not
      // know" — is the honest state for a payload that is not an object, and it is the
      // state a 5xx already produces, so every reader's existing null handling covers
      // it. It does NOT demand particular fields: this endpoint's projection is not this
      // module's business, and a stricter gate here would turn a renamed field into a
      // practice that vanished.
      const doc = (data && typeof data === "object" && !Array.isArray(data)) ? data : null;
      if (data && !doc) {
        // Not `unavailable`: that flag means "the platform is deliberately not serving
        // this practice" (BUG-041) and drives a different screen. This is our own
        // failure to read the answer.
        console.error(
          `TenantContext: /saas/tenant/${effectiveSlug} answered a ${typeof data}, not a `
          + `tenant record — treating the practice as unresolved`);
      }
      setTenant(doc);
      // Cleared on every load, not only set: a reactivated practice must come back
      // without a full page reload.
      setUnavailable(false);
    } catch (e) {
      setTenant(null);
      setUnavailable(isTenantSuspendedError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveSlug]);

  return (
    <TenantContext.Provider
      value={{
        tenant,
        loading,
        isDefault: effectiveSlug === "emilie",
        slug: effectiveSlug,
        unavailable,
        refresh: load,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => useContext(TenantContext);

/**
 * BUG-039 — the "go home" destination for a public page, correct in all three hosting
 * contexts a tenant's site can be reached through:
 *
 *   1. the legacy `/t/{slug}/...` path (transitional): home is the prefix itself,
 *      "/t/{slug}" — TenantSite's own index route lives there.
 *   2. a genuine tenant host (custom domain or `<label>.PLATFORM_DOMAIN` subdomain):
 *      home is "/" — the tenant's site already IS the root of that host.
 *   3. the platform host itself (apex, localhost, preview hosts), serving the demo
 *      practice directly, with no `/t/{slug}` prefix: home is "/demo" — plain "/" is the
 *      SaaS marketing page (PlatformLanding), not a practice site. This is the defect
 *      BUG-039 reported: CheckoutSuccess.jsx hardcoded `Link to="/"`.
 *
 * SILENCE IS NO LONGER READ AS "SOME OTHER PRACTICE" (BUG-039 re-open, 2026-09-09).
 * Branch 3 used to be `tenant?.is_root ? "/demo" : "/"`, so a tenant document that had
 * not loaded yet, had failed to load, or was not even an object (a proxy answering
 * index.html for an unknown /api path) all resolved to "/" — the SaaS marketing page.
 * `tenantHomePath` (lib/tenantPath.js) now answers "/demo" for every such absence and
 * still honours an `is_root: false` document that is actually present.
 *
 * AdminLayout.jsx's "View site" link encodes the same precedence from the tenant's
 * SAVED fields (`tenantSiteUrl`, lib/tenantHost.js); this is its public-page
 * counterpart, sourced from the actual current host and path instead.
 *
 * The rule itself lives in `tenantHomePath` (lib/tenantPath.js) — a pure function, so
 * `siteLinks.buildNavModel` (no hook context) applies the exact same one to the chrome
 * logos and footer links.
 */
export function useTenantHomePath() {
  const { tenant } = useTenant();
  return tenantHomePath(tenant);
}
